/**
 * Bid engine — deterministic cleaning-bid math.
 *
 * This is the single source of truth for every dollar a bid produces.
 * The phase-2 AI intake layer NEVER computes money; it only fills
 * `BidInputs`, which this engine turns into a `BidComputation`. Keeping
 * all pricing here means a bid is reproducible and defensible line-by-
 * line — the same inputs always yield the same number.
 *
 * Money is integer cents end to end (matches the rest of the codebase,
 * e.g. requested_amount_cents). Formatting to dollars happens only at the
 * UI edge via `formatUsdFromCents`.
 *
 * Default numbers for the shipped presets are sourced in
 * docs/bidding/houston-student-turnover-rates-2026.md (CLAUDE.md rule 14).
 */

export interface BidInputs {
  /** Number of units / spaces in the job (1 for a single space). */
  unitCount: number
  /** Base clean time per unit, in minutes, before add-ons and soil. */
  baseLaborMinutesPerUnit: number
  /** Extra per-unit minutes (2nd bath, appliance deep-clean, …), before soil. */
  addOnMinutesPerUnit: number
  /** Condition/soil factor applied to (base + add-on) labor time. 1.0 = normal. */
  soilMultiplier: number
  /** Crew members working a unit concurrently — divides labor into wall-clock. */
  crewSize: number
  /** Fully-burdened labor cost per hour, in cents. */
  burdenedHourlyRateCents: number
  /** Suggested bid = labor cost × this. >1 adds margin over burdened labor. */
  markupMultiplier: number
  /** One-off materials/equipment for the whole job, in cents (passed at cost). */
  flatMaterialsCents: number
}

export interface BidComputation {
  unitCount: number
  /** Effective labor minutes for one unit, after add-ons and soil. */
  perUnitLaborMinutes: number
  totalLaborMinutes: number
  totalLaborHours: number
  /** Total labor hours ÷ crew size — calendar time on site. */
  wallClockHours: number
  laborCostCents: number
  materialsCents: number
  totalCostCents: number
  suggestedBidCents: number
  perUnitBidCents: number
  /** (bid − total cost) ÷ bid, in the range 0..1. 0 when the bid is 0. */
  grossMarginPct: number
}

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback
}

/** Coerce to a finite, non-negative number (NaN / Infinity / negative → 0). */
function nonNeg(value: number): number {
  const v = finiteOr(value, 0)
  return v < 0 ? 0 : v
}

/**
 * Turn bid inputs into a full computation. Pure and total: every field is
 * sanitised so a half-typed form (empty input → NaN) can never produce a
 * NaN dollar figure on screen.
 */
export function computeBid(inputs: BidInputs): BidComputation {
  const unitCount = Math.floor(nonNeg(inputs.unitCount))
  const base      = nonNeg(inputs.baseLaborMinutesPerUnit)
  const addOn     = nonNeg(inputs.addOnMinutesPerUnit)
  const soil      = nonNeg(inputs.soilMultiplier)
  const crew      = Math.max(1, Math.floor(finiteOr(inputs.crewSize, 1)))
  const rate      = nonNeg(inputs.burdenedHourlyRateCents)
  const markup    = nonNeg(inputs.markupMultiplier)
  const materials = Math.round(nonNeg(inputs.flatMaterialsCents))

  const perUnitLaborMinutes = (base + addOn) * soil
  const totalLaborMinutes   = perUnitLaborMinutes * unitCount
  const totalLaborHours     = totalLaborMinutes / 60
  const wallClockHours      = totalLaborHours / crew

  const laborCostCents    = Math.round(totalLaborHours * rate)
  const totalCostCents    = laborCostCents + materials
  // Markup applies to labor only; materials pass through at cost.
  const suggestedBidCents = Math.round(laborCostCents * markup) + materials
  const perUnitBidCents   = unitCount > 0 ? Math.round(suggestedBidCents / unitCount) : 0
  const grossMarginPct    = suggestedBidCents > 0
    ? (suggestedBidCents - totalCostCents) / suggestedBidCents
    : 0

  return {
    unitCount,
    perUnitLaborMinutes,
    totalLaborMinutes,
    totalLaborHours,
    wallClockHours,
    laborCostCents,
    materialsCents: materials,
    totalCostCents,
    suggestedBidCents,
    perUnitBidCents,
    grossMarginPct,
  }
}

/** Format integer cents as a USD string, e.g. 34965 → "$349.65". */
export function formatUsdFromCents(cents: number): string {
  const dollars = finiteOr(cents, 0) / 100
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}
